<script setup lang="ts">
import { mdiAlert } from '@quasar/extras/mdi-v7';
import { computed, nextTick, ref, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useQuasar } from 'quasar';
import { useStepData } from '../../useStepData';
import type { TheoryContent } from 'types/course/step';
import StepInfoCard from '../../components/Theory/StepInfoCard/StepInfoCard.vue';
import StepHeader from '../../components/StepHeader/StepHeader.vue';
import StepFooter from '../../components/StepFooter/StepFooter.vue';
import StepCard from '../../components/StepCard/StepCard.vue';
import BDialog from 'shared/components/base/BDialog/BDialog.vue';
import InfoDialogCard from 'src/shared/components/InfoDialogCard/InfoDialogCard.vue';
import StepTheoryCard from '../../components/Theory/StepTheoryCard/StepTheoryCard.vue';
import DOMPurify from 'dompurify';
import { useHeaderActions } from 'src/shared/composables/useHeaderActions/useHeaderActions';
import { useEvents } from 'src/shared/composables/useEvents/useEvents';

const { listen } = useEvents();

interface Props {
  isCompleted?: boolean;
}

const props = defineProps<Props>();

const route = useRoute();

const stepDocId = route.params.stepDocId as string;

const { isLoading, loadError, validationErrors, documentStepRepo, getStep } =
  useStepData(stepDocId);

useHeaderActions({
  eventActions: ['action:instruction'],
  title: documentStepRepo.value?.step_name || '',
});
listen('action:instruction', () => (isInfoDialog.value = true));

getStep()
  .then()
  .catch((error) => {
    console.error(error);
  });

const theoryContent = computed(() => {
  return (
    documentStepRepo.value?.step_type?.[0]?.theory_content ??
    ([] as TheoryContent[])
  );
});
const step = ref(0);

const isInfoDialog = ref(false);

const sanitizedInstructions = computed(() => {
  return documentStepRepo.value?.instructions
    ? DOMPurify.sanitize(documentStepRepo.value.instructions)
    : '';
});

function reloadPage() {
  window.location.reload();
}

const $q = useQuasar();
watchEffect(() => {
  if (isLoading.value) $q.loading.show();
  else $q.loading.hide();
});

const emits = defineEmits<{
  'step:complete': [];
  'step:start': [dateTime: string];
  'action:completion': [];
}>();

watch(
  () => step.value,
  (newValue: number, prevValue: number) => {
    if (prevValue === 0 && newValue === 1) {
      emits('step:start', new Date().toISOString());
    }
  },
);
function handleStepCompletion() {
  if (!props.isCompleted) emits('step:complete');
  void nextTick().then(() => {
    emits('action:completion');
  });
}
</script>

<template>
  <!-- Error States -->
  <div
    v-if="
      loadError ||
      validationErrors.length > 0 ||
      (!documentStepRepo && !isLoading)
    "
    class="column col-grow items-center justify-center q-pa-lg"
  >
    <!-- Loading Error -->
    <q-banner
      v-if="loadError"
      class="bg-negative text-white q-mb-md"
      rounded
    >
      <template #avatar>
        <q-icon
          name="error"
          size="lg"
        />
      </template>
      <div class="text-h6 q-mb-sm">{{ $t('STEP_LOAD_ERROR') }}</div>
      <div class="text-body2">{{ loadError }}</div>
      <template #action>
        <q-btn
          flat
          color="white"
          :label="$t('TRY_AGAIN')"
          @click="reloadPage"
        />
        <q-btn
          flat
          color="white"
          :label="$t('GO_BACK')"
          @click="$router.back()"
        />
      </template>
    </q-banner>

    <!-- Validation Errors -->
    <q-banner
      v-else-if="validationErrors.length > 0"
      class="bg-warning text-dark q-mb-md"
      rounded
    >
      <template #avatar>
        <q-icon
          :name="mdiAlert"
          size="lg"
        />
      </template>
      <div class="text-subtitle1 q-mb-sm">{{ $t('STEP_INCOMPLETE_DATA') }}</div>
      <ul class="q-pl-md">
        <li
          v-for="(error, index) in validationErrors"
          :key="index"
        >
          {{ error }}
        </li>
      </ul>
      <template #action>
        <q-btn
          flat
          color="dark"
          :label="$t('RETRY')"
          @click="reloadPage"
        />
      </template>
    </q-banner>

    <!-- Step Not Found -->
    <q-banner
      v-else
      class="bg-negative text-white"
      rounded
    >
      <template #avatar>
        <q-icon
          name="error"
          size="lg"
        />
      </template>
      {{ $t('STEP_NOT_FOUND') }}
      <template #action>
        <q-btn
          flat
          color="white"
          :label="$t('GO_BACK')"
          @click="$router.push({ name: 'dashboard' })"
        />
      </template>
    </q-banner>
  </div>

  <!-- Main Content -->
  <StepCard
    v-else-if="documentStepRepo"
    :step-category="documentStepRepo.step_category"
    class="column col-grow no-wrap full-width full-height"
    flat
  >
    <!-- Header -->
    <template #header>
      <StepHeader
        class="text-dark q-px-sm q-py-none full-width"
        :class="{
          'q-px-md': $q.screen.gt.sm,
        }"
        :is-theory="true"
        :title="documentStepRepo?.step_name ?? ''"
        :category="documentStepRepo?.step_category ?? ''"
      />
    </template>

    <q-stepper
      v-if="theoryContent"
      v-model="step"
      class="scroll-y q-pa-none"
      :vertical="$q.platform.is.mobile"
      animated
      done-color="secondary"
      active-color="primary"
      inactive-color="primary"
      flat
      header-nav
    >
      <template
        v-for="(content, index) of theoryContent"
        :key="content.id"
      >
        <q-step
          :name="index"
          :title="content?.title ?? ''"
          animated
          class="q-pa-none"
          :class="{ 'no-inner-padding': $q.platform.is.mobile }"
          :done="step > index"
        >
          <StepTheoryCard
            :content="content"
            class="scroll"
          />
        </q-step>
      </template>
    </q-stepper>

    <q-space></q-space>

    <BDialog
      v-model:is-open="isInfoDialog"
      position="bottom"
      card-class="bg-info dialog-surface"
      title="INSTRUCTIONS"
      @hide="isInfoDialog = false"
    >
      <InfoDialogCard v-if="documentStepRepo?.instructions">
        <StepInfoCard :instructions="sanitizedInstructions"></StepInfoCard>
      </InfoDialogCard>
    </BDialog>

    <template #footer>
      <q-space />
      <StepFooter class="text-dark">
        <template #button-start>
          <q-btn
            v-if="step > 0 && step <= theoryContent.length - 1"
            color="secondary"
            text-color="dark"
            :label="$t('BACK')"
            class="q-ml-sm"
            @click="step = step - 1"
          />
        </template>
        <template #button-end>
          <q-btn
            v-if="step < theoryContent.length - 1"
            color="secondary"
            text-color="dark"
            :label="$t('NEXT')"
            @click="step = step + 1"
          />
          <q-btn
            v-if="step === theoryContent.length - 1"
            color="positive"
            :label="$t('COMPLETE_STEP')"
            class="q-ml-sm"
            @click="handleStepCompletion"
          />
        </template>
      </StepFooter>
    </template>
  </StepCard>
</template>

<style>
.q-stepper--vertical .no-inner-padding .q-stepper__step-inner {
  padding: 0;
}
</style>
